import Checkbox from '@material-ui/core/Checkbox'
import CircularProgress from '@material-ui/core/CircularProgress'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Divider from '@material-ui/core/Divider'
import Grid from '@material-ui/core/Grid'
import IconButton from '@material-ui/core/IconButton'
import InputAdornment from '@material-ui/core/InputAdornment'
import React, { useState, useEffect, useRef } from 'react'
import TextField from '@material-ui/core/TextField'
import Autocomplete from '@material-ui/lab/Autocomplete'
import SearchIcon from '@material-ui/icons/Search'
import makeStyles from '@material-ui/core/styles/makeStyles'

import { strings, errors as errorUtils, validators } from '../../../../../../utils'

import { FormControl } from '../../../../../../components/Forms'
import { Alert } from '../../../../../../components'
import { addressService } from '../../../../../../services'

const { limit, keepOnlyNumbers, upcase } = strings

const useStyles = makeStyles(() => ({
  divider: {
    margin: '20px 0',
  },
  alert: {
    margin: '16px 0',
  },
}))

const handleText = text => upcase(limit(text, 250))

const formatStreetOption = street =>
  `${street.name} - ${street.neighborhood}${
    street.zipcode ? ` - CEP ${street.zipcode}` : ''
  }`

const Form = ({ address, errors, loading, setLoading, validating, update }) => {
  const classes = useStyles()

  const [_errors, _setErrors] = useState({})
  const [streetSearch, setStreetSearch] = useState(address.street || '')
  const [streetResults, setStreetResults] = useState([])
  const [streetSearched, setStreetSearched] = useState(false)

  const hasError = errorUtils.hasError(_errors)
  const errorMessage = errorUtils.errorMessage(_errors)

  const isUnifiedCity = () => !!address.cityZipcode && !address.weakStreetId
  const needsStreetSearch = () => isUnifiedCity() || address.weak

  // Com mais de um Endereço Extra no CEP, bairro e logradouro já são
  // escolhidos nos seletores da busca; repetir os campos aqui duplicaria.
  const usesWeakStreetSelectors = () =>
    !!address.weakStreetId &&
    address.source === 'weakStreet' &&
    (address.results || []).length > 1

  const isStreetSelected = () => {
    if (isUnifiedCity() || address.weak) {
      return (
        validators.addressTextIsValid(address.street) &&
        validators.addressTextIsValid(address.neighborhood)
      )
    }

    return (
      !!address.streetId ||
      !!address.weakStreetId ||
      validators.addressTextIsValid(address.street)
    )
  }

  const streetLengthError = () => {
    if (!address.street || validators.addressTextIsValid(address.street)) {
      return ''
    }

    return `Informe pelo menos ${validators.ADDRESS_TEXT_MIN_LENGTH} caracteres`
  }

  const neighborhoodLengthError = () => {
    if (!address.neighborhood || validators.addressTextIsValid(address.neighborhood)) {
      return ''
    }

    return `Informe pelo menos ${validators.ADDRESS_TEXT_MIN_LENGTH} caracteres`
  }

  const shouldDisplayAlert = () => address.weak || isUnifiedCity()

  const shouldDisableManualInput = () =>
    (!address.weak && !isUnifiedCity()) || validating

  const isStreetFromSelection = () =>
    !!address.streetId || !!address.weakStreetId

  const shouldShowNumberFields = () => isStreetSelected()

  const showManualStreetHint = () =>
    needsStreetSearch() &&
    streetSearch.trim().length >= 3 &&
    !loading &&
    streetSearched &&
    !streetResults.length

  const manualInputProps = () =>
    shouldDisableManualInput() && address.street ? { shrink: true } : {}

  const lastStreetSearch = useRef(0)
  const pendingStreetSearches = useRef(0)

  const tryToFindStreet = async term => {
    const query = handleText(term).trim()
    const searchId = ++lastStreetSearch.current

    if (!address.cityId || query.length < 3) {
      setStreetResults([])
      return
    }

    setStreetSearched(true)

    pendingStreetSearches.current += 1
    setLoading(true)

    const {
      success,
      payload: { addresses },
    } = await addressService.searchAddressByCityAndZipCodeOrStreet(
      address.cityId,
      query
    )

    pendingStreetSearches.current -= 1

    if (success && searchId === lastStreetSearch.current) {
      setStreetResults(addresses)
    }

    if (pendingStreetSearches.current === 0) {
      setLoading(false)
    }
  }

  const searchStreet = () => tryToFindStreet(streetSearch)

  const canSearchStreet = () =>
    !validating &&
    !loading &&
    !!address.cityId &&
    handleText(streetSearch).trim().length >= 3

  const chooseStreet = street => {
    update({
      streetId: street.id,
      weakStreetId: undefined,
      street: street.name,
      neighborhood: street.neighborhood,
      zipcode: street.zipcode || address.zipcode,
    })
    setStreetSearch(street.name)
  }

  useEffect(() => {
    _setErrors(errors)
  }, [errors])

  useEffect(() => {
    if (!needsStreetSearch()) return

    setStreetSearch(address.street || '')
    setStreetResults([])
    setStreetSearched(false)
  }, [address.cityId])

  const renderStreetAutocomplete = () => (
    <Grid item md={9} sm={8} xs={12}>
      <FormControl>
        <Autocomplete
          freeSolo={needsStreetSearch()}
          filterOptions={options => options}
          disablePortal
          // sem `loading`: desabilitar tira o foco e as sugestões não abrem
          disabled={validating || (address.weak && !address.cityId)}
          loading={loading}
          loadingText="Buscando..."
          openText="Abrir"
          closeText="Fechar"
          clearText="Limpar"
          noOptionsText={
            streetSearched
              ? 'Nenhum logradouro encontrado. Digite o endereço manualmente.'
              : 'Digite o endereço e toque em pesquisar'
          }
          options={streetResults}
          getOptionLabel={option =>
            typeof option === 'string' ? option : option.name || ''
          }
          renderOption={option =>
            typeof option === 'string' ? option : formatStreetOption(option)
          }
          inputValue={streetSearch}
          onInputChange={(_, value, reason) => {
            if (reason === 'reset') return

            const text = handleText(value)
            setStreetSearch(text)
            setStreetResults([])
            setStreetSearched(false)

            if (reason === 'input' || reason === 'clear') {
              update({
                street: text,
                streetId: undefined,
                weakStreetId: undefined,
                neighborhood: isStreetFromSelection() ? '' : address.neighborhood,
              })
            }
          }}
          onChange={(_, value) => {
            if (value && typeof value !== 'string') {
              chooseStreet(value)
            } else if (typeof value === 'string') {
              update({ street: handleText(value) })
            }
          }}
          value={address.street || ''}
          renderInput={params => (
            <TextField
              {...params}
              error={
                hasError('streetStr') ||
                hasError('streetId') ||
                !!streetLengthError()
              }
              helperText={
                errorMessage('streetStr') ||
                errorMessage('streetId') ||
                streetLengthError() ||
                (showManualStreetHint()
                  ? 'Nenhum logradouro encontrado na base. Digite o endereço manualmente.'
                  : '')
              }
              InputLabelProps={address.street ? { shrink: true } : {}}
              label={isUnifiedCity() ? 'Nome da rua' : 'Rua'}
              variant="outlined"
              inputProps={{
                ...params.inputProps,
                enterKeyHint: 'search',
                onKeyDown: e => {
                  if (e.key !== 'Enter' || streetResults.length) return

                  e.preventDefault()
                  if (canSearchStreet()) searchStreet()
                },
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        aria-label="Pesquisar rua"
                        title="Pesquisar"
                        disabled={!canSearchStreet()}
                        onClick={searchStreet}
                      >
                        {loading ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          <SearchIcon fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </FormControl>
    </Grid>
  )

  const renderStreetField = () => {
    if (needsStreetSearch()) {
      return renderStreetAutocomplete()
    }

    return (
      <Grid item md={9} sm={8} xs={12}>
        <FormControl>
          <TextField
            error={
              hasError('streetStr') ||
              hasError('streetId') ||
              !!streetLengthError()
            }
            helperText={
              errorMessage('streetStr') ||
              errorMessage('streetId') ||
              streetLengthError()
            }
            InputLabelProps={manualInputProps()}
            disabled={shouldDisableManualInput()}
            label="Rua"
            variant="outlined"
            name="street"
            value={address.street || ''}
            onChange={e => update({ street: handleText(e.target.value) })}
          />
        </FormControl>
      </Grid>
    )
  }

  const renderNeighborhoodField = () => (
    <Grid item md={3} sm={8} xs={12}>
      <FormControl>
        <TextField
          error={hasError('neighborhood') || !!neighborhoodLengthError()}
          helperText={errorMessage('neighborhood') || neighborhoodLengthError()}
          InputLabelProps={
            address.neighborhood ? { shrink: true } : manualInputProps()
          }
          disabled={isStreetFromSelection() || shouldDisableManualInput()}
          label="Bairro"
          variant="outlined"
          name="neighborhood"
          value={address.neighborhood || ''}
          onChange={e =>
            update({ neighborhood: handleText(e.target.value) })
          }
        />
      </FormControl>
    </Grid>
  )

  return (
    <>
      <Grid container spacing={1}>
        {shouldDisplayAlert() ? (
          <Grid item xs={12} className={classes.alert}>
            <Alert>
              <strong>Atenção!</strong>
              <br />
              Confirme se todas as informações do seu endereço estão corretas.
              Caso o endereço esteja incorreto ou incompleto, o sistema poderá
              gerar resultados imprecisos na alocação do(a) estudante.
            </Alert>
          </Grid>
        ) : (
          <Grid item xs={12}>
            <Divider className={classes.divider} />
          </Grid>
        )}

        {!usesWeakStreetSelectors() && renderStreetField()}
        {!usesWeakStreetSelectors() && renderNeighborhoodField()}

        {shouldShowNumberFields() && (
          <>
            <Grid item md={3} sm={4} xs={12}>
              <FormControl>
                <TextField
                  error={hasError('number')}
                  helperText={errorMessage('number')}
                  disabled={address.withoutNumber || validating}
                  label="Número"
                  variant="outlined"
                  name="number"
                  value={address.number || ''}
                  onChange={e =>
                    update({ number: keepOnlyNumbers(e.target.value) })
                  }
                />
              </FormControl>
            </Grid>

            <Grid item md={6} xs={12}>
              <FormControl>
                <TextField
                  disabled={validating}
                  label="Complemento (opcional)"
                  variant="outlined"
                  name="complement"
                  value={address.complement || ''}
                  onChange={e =>
                    update({ complement: handleText(e.target.value) })
                  }
                />
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={address.withoutNumber || false}
                    disabled={validating}
                    onChange={e => {
                      let hash = { ..._errors }

                      if (e.target.checked) {
                        delete hash['number']
                      } else if (errors.number) {
                        hash = { ...hash, number: errors.number }
                      }

                      _setErrors(hash)

                      update({
                        withoutNumber: e.target.checked,
                        number: undefined,
                      })
                    }}
                    name="withoutNumber"
                    color="primary"
                  />
                }
                label="Endereço não possui número"
              />
            </Grid>
          </>
        )}
      </Grid>
    </>
  )
}

export default Form
